import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { BOARD_Z_INDEX } from "../../../app/utils/projectBoard/boardDesignTokens";
import { BoardButton, BoardIconButton } from "../../atoms/BoardDesignSystem";
import "./BoardEstimatedHoursModal.scss";

const BoardEstimatedHoursModal = ({
  open,
  cardTitle,
  sourceListName = "To Do",
  onClose,
  onConfirm,
  loading = false,
}) => {
  const [estimatedHours, setEstimatedHours] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setEstimatedHours("");
    setError("");
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !loading) onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, loading, onClose]);

  if (!open) return null;

  const handleConfirm = async () => {
    const trimmed = estimatedHours.trim();
    const parsed = trimmed === "" ? NaN : Number(trimmed);
    if (trimmed === "" || Number.isNaN(parsed) || parsed < 0) {
      setError("Enter a valid non-negative number of hours.");
      return;
    }
    setError("");
    await onConfirm?.(parsed);
  };

  return createPortal(
    <div
      className="pb-modal__overlay"
      onClick={() => !loading && onClose?.()}
      role="presentation"
    >
      <div
        className="pb-modal__dialog pb-estimated-hours-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="board-estimated-hours-title"
        onClick={(e) => e.stopPropagation()}
        style={{ zIndex: BOARD_Z_INDEX.modal + 5 }}
      >
        <div className="pb-estimated-hours-modal__header">
          <h2 id="board-estimated-hours-title" className="pb-estimated-hours-modal__title">
            Estimated hours required
          </h2>
          <BoardIconButton icon="✕" label="Close" onClick={onClose} disabled={loading} />
        </div>

        <p className="pb-estimated-hours-modal__subtitle">
          Moving <strong>{cardTitle || "this card"}</strong> from <strong>{sourceListName}</strong>.
          Add estimated hours before this card can be moved.
        </p>

        <div className="pb-estimated-hours-modal__field">
          <label className="pb-estimated-hours-modal__label" htmlFor="board-estimated-hours-input">
            Estimated hours
          </label>
          <div className="pb-estimated-hours-modal__input-wrap">
            <input
              id="board-estimated-hours-input"
              type="number"
              className="pb-estimated-hours-modal__input"
              min="0"
              step="0.25"
              value={estimatedHours}
              onChange={(e) => {
                setEstimatedHours(e.target.value);
                if (error) setError("");
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleConfirm();
              }}
              placeholder="e.g. 4"
              disabled={loading}
              autoFocus
            />
            <span className="pb-estimated-hours-modal__suffix">hrs</span>
          </div>
          {error && <p className="pb-estimated-hours-modal__error">{error}</p>}
        </div>

        <div className="pb-estimated-hours-modal__actions">
          <BoardButton variant="ghost-dark" size="sm" onClick={onClose} disabled={loading}>
            Cancel
          </BoardButton>
          <BoardButton variant="primary" size="sm" onClick={handleConfirm} disabled={loading}>
            {loading ? "Saving..." : "Save & move card"}
          </BoardButton>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BoardEstimatedHoursModal;
